import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, FileText, Package, DollarSign, AlertTriangle, CheckCircle,
  ChevronLeft, ChevronRight, ChevronDown, Search, RefreshCw, X, Clock,
  FileWarning, HelpCircle, Scale, DownloadCloud, Info, Check, Undo2, Receipt,
} from 'lucide-react';
import {
  fetchUpsBills, fetchUpsBillsSummary, fetchUpsBillInvoice,
  fetchUpsBillCharges, importUpsBill, uploadUpsBillPdf, upsBillPdfUrl,
  fetchUpsBillReconciliation, fetchUpsBillReconSummary, fetchUpsBillExtras,
  fetchUpsBillAccountCharges, fetchUpsBillSummaryOfCharges,
  startUpsBillBackfill, fetchUpsBackfillStatus, apiProxyGet,
  acceptUpsBillCharges, unacceptUpsBillCharges,
  fetchUpsBillExplainedSummary, bulkAcceptUpsBillCharges,
  startUpsRefundScan, fetchUpsRefundScanStatus, fetchUpsRefunds, setUpsRefundStatus, setUpsRefundResolution, matchUpsRefundCredits,
  fetchOrderProblems, fetchOrderSplitInfo,
} from '../services/api';
import { getCurrentUser } from '../services/auth';
import { NewClaimModal } from './UpsClaims';

// An order's Auth.net transactions, straight from production (via the api proxy)
// — the same feed the claim modal uses. A refund shows a row whose cardType is
// "Refunded"/"Credited". Used to auto-detect refunded orders in the Late
// Packages list so they default to "ready to file" instead of "Waiting".
// Goes through apiProxyGet, not a bare fetch — the /api proxy is session-gated
// (it forwards with production admin rights), so the call needs the staff token.
async function fetchOrderPaymentsRaw(orderId) {
  return apiProxyGet('/Orders/OrderPaymentsByOrderId', { orderId });
}
const paymentsShowRefund = (rows) => Array.isArray(rows) && rows.some(p =>
  /refund|credit/i.test(String(p.cardType || '')) || /#(refund|credit)/i.test(String(p.transactionId || '')));

const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = n => (Number(n) > 0 ? '+' : '') + fmt(n);
// DATE columns arrive as "YYYY-MM-DD..." serialized at UTC midnight; feeding
// that to new Date() renders the PREVIOUS day in Eastern time (a 6/6 invoice
// displayed as Jun 5). Parse the date part as local instead.
const fmtDate = v => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
// The BC order number is the one staff recognize, but phone / manually-created
// orders have no BigCommerce number at all and carry bc_order_id '0' — those
// rendered as a bare "0" and read as "we don't know this order" when in fact
// the order is right there under its internal id. Fall back to it, labeled.
const OrderNo = ({ row }) => {
  const bc = row.bc_order_id == null ? '' : String(row.bc_order_id).trim();
  const internal = row.order_id == null ? '' : String(row.order_id).trim();
  const byRef = row.matched_by_ref ? (
    <span
      title="Matched by the order number printed on the shipping label — the tracking number recorded on this order does not match the one UPS billed."
      style={{ fontSize: 9, color: 'var(--orange)', marginLeft: 3 }}
    >ref</span>
  ) : null;
  if (bc && bc !== '0') return <>{bc}{byRef}</>;
  if (internal) return (
    <>
      <span title="No BigCommerce order number (phone or manually created order) — this is the internal order id.">
        {internal}<span style={{ fontSize: 9, color: 'var(--gray-400)', marginLeft: 3 }}>int</span>
      </span>{byRef}
    </>
  );
  return <span style={{ color: 'var(--gray-400)' }}>—</span>;
};
// Pull the clock time out of a "YYYY-MM-DD HH:MM:SS" timestamp -> "12:22 PM".
// Returns '' for date-only values.
const fmtClock = ts => {
  const m = String(ts || '').match(/[ T](\d{2}):(\d{2})/);
  if (!m) return '';
  let h = parseInt(m[1], 10); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
};

// "2026-06-13 12:22:55" -> "6/13/26 12:22PM" (date-only -> "6/13/26").
const fmtDateTime = v => {
  const s = String(v || '');
  const dm = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dm) return v || '—';
  const date = `${Number(dm[2])}/${Number(dm[3])}/${dm[1].slice(2)}`;
  const tm = s.match(/[ T](\d{2}):(\d{2})/);
  if (!tm) return date;
  let h = parseInt(tm[1], 10); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
  return `${date} ${h}:${tm[2]}${ap}`;
};

const CAT_LABEL = { FRT: 'Transportation', FSC: 'Fuel Surcharge', ACC: 'Accessorial', INF: 'Informational' };
const CAT_COLOR = { FRT: 'var(--navy)', FSC: '#b8860b', ACC: 'var(--orange)', INF: 'var(--gray-400)' };

// Reconciliation status — compares CORE billed (standard charges) vs scale.
// Adjustments/extras are NOT a status; they're surfaced in the Extras tab.
const STATUS = {
  ok:       { label: 'OK',       color: '#1a8a4a', bg: '#e6f4ea', icon: CheckCircle,   blurb: 'Core charges match the scale, on time' },
  late:     { label: 'Late',     color: '#c62828', bg: '#fde8e8', icon: Clock,         blurb: 'Delivered late — claim/credit' },
  variance: { label: 'Cost off', color: '#b8860b', bg: '#fff8e1', icon: AlertTriangle, blurb: 'Core billed ≠ scale beyond tolerance' },
  no_cost:  { label: 'No cost',  color: 'var(--gray-500)', bg: '#eef0f2', icon: HelpCircle, blurb: 'Order found, no scale cost' },
  no_data:  { label: 'No order', color: '#5b6470', bg: '#eceff1', icon: HelpCircle,    blurb: 'Billed, no order on file (run Backfill)' },
};
const STATUS_ORDER = ['no_data', 'late', 'variance', 'no_cost', 'ok'];

const TOL_PRESETS = [
  { label: 'Penny-exact', v: 0 },
  { label: '± $0.01', v: 0.01 },
  { label: '± $0.50', v: 0.5 },
  { label: '± $1.00', v: 1 },
];

// Friendly labels for known account-level (blank-tracking) fees, matched on
// the UPS charge description. The amounts change every cycle, so we identify
// them by pattern, not value. "Billing Adjustment for {month}" is UPS's
// monthly API / Delivery-Photo (Track Alert with Photo) usage charge.
const ACCOUNT_FEE_LABELS = [
  { test: d => /billing adjustment for/i.test(d), label: 'API / Delivery-Photo usage', note: 'UPS Track Alert with Photo + API usage — billed monthly, amount varies by volume.' },
  { test: (d, c) => c === '205' || /service charge/i.test(d), label: 'Weekly pickup / service fee', note: 'UPS account service charge (e.g. scheduled pickup).' },
];
const accountFeeLabel = (description, classCode) => {
  const hit = ACCOUNT_FEE_LABELS.find(f => f.test(String(description || ''), String(classCode || '')));
  return hit || null;
};
const PAGE_SIZE = 250;

// True only when the page was loaded via a browser reload (F5/Ctrl-R), not via
// in-app navigation. Stays constant for the page's lifetime.
function isPageReload() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav) return nav.type === 'reload';
    return performance.navigation && performance.navigation.type === 1;
  } catch { return false; }
}
// Set true after UpsBills' first mount of this page-load. Clicking "Bills" in the
// nav remounts the component (App.js bumps pageKey), but this stays true — so the
// "reopen the bill" restore only fires on a real reload, not on a nav click.
let upsBillsBooted = false;

export default function UpsBills() {
  // Seed from sessionStorage so a hard-refresh inside a bill stays on that bill —
  // but ONLY on a genuine reload, and only the first mount (not nav remounts).
  const [view, setView] = useState(() => {
    try {
      if (!upsBillsBooted && isPageReload() && sessionStorage.getItem('upsBillActiveInvoice')) return 'detail';
    } catch (_) {}
    return 'list';
  });          // 'list' | 'detail'
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [claimModal, setClaimModal] = useState(null);   // prefillOrder for the in-page File-Claim modal
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // upload
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // detail / reconciliation
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [detailTab, setDetailTab] = useState('reconcile');  // 'reconcile' | 'extras'
  const [recon, setRecon] = useState([]);
  const [reconSummary, setReconSummary] = useState({ byStatus: {}, total: null });
  const [tolerance, setTolerance] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');  // bound to the field (instant)
  const [search, setSearch] = useState('');             // debounced — drives the query
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  // Staged import files — a bill is a CSV + the PDF invoice (see stageFiles).
  const [pendingCsv, setPendingCsv] = useState(null);
  const [pendingPdf, setPendingPdf] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);
  const attachRef = useRef(null);
  const attachTargetRef = useRef(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [expandedExtra, setExpandedExtra] = useState(null);
  const [expandedLines, setExpandedLines] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [extras, setExtras] = useState([]);
  const [extrasLoading, setExtrasLoading] = useState(false);
  // Account-level charges (no tracking #) — the fees billed to the account
  // rather than a package. accountCharges is a single object (footing + lines).
  const [accountCharges, setAccountCharges] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [summaryOfCharges, setSummaryOfCharges] = useState(null);  // UPS 4-bucket Summary of Charges
  // Late-delivery refund/claim tracker
  const [refunds, setRefunds] = useState([]);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundScan, setRefundScan] = useState(null);
  // order_ids that have a refund/credit transaction in production (Auth.net) —
  // filled in the background after the list loads so refunded rows default to
  // "ready to file" instead of "Waiting".
  const [refundOrderIds, setRefundOrderIds] = useState(() => new Set());
  const [copiedTracking, setCopiedTracking] = useState(null);
  const refundPollRef = useRef(null);
  const [expandedRefund, setExpandedRefund] = useState(null);     // tracking # of the open row
  const [refundDetail, setRefundDetail] = useState({});           // tracking# -> { loading, problems, split }

  // Expand a Late-Packages row: pull the order's Problems + split/reship linkage
  // live (Neon's mirror is stale), so staff can decide how to file.
  const toggleRefundDetail = async (r) => {
    if (expandedRefund === r.tracking_number) { setExpandedRefund(null); return; }
    setExpandedRefund(r.tracking_number);
    if (refundDetail[r.tracking_number] || !r.order_id) return;   // cached, or no internal id to look up
    setRefundDetail(d => ({ ...d, [r.tracking_number]: { loading: true } }));
    const [problems, split] = await Promise.all([
      fetchOrderProblems(r.order_id).catch(() => []),
      fetchOrderSplitInfo(r.order_id).catch(() => ({})),
    ]);
    setRefundDetail(d => ({ ...d, [r.tracking_number]: { loading: false, problems: Array.isArray(problems) ? problems : [], split } }));
  };
  // Service Refund section of the bill = UPS GSR credits for prior-week lates.
  const [serviceRefunds, setServiceRefunds] = useState([]);
  const [serviceRefundsLoading, setServiceRefundsLoading] = useState(false);
  const [creditMatches, setCreditMatches] = useState([]);
  // SIM-credit summary + bulk-accept (explained Cost-off rows)
  const [explained, setExplained] = useState(null);
  const [bulkAccepting, setBulkAccepting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  // Accept UPS charges: tracking_numbers currently checked + in-flight flag.
  // Mirrors the legacy admin's "accept the billed amount as-is" reconcile.
  const [selected, setSelected] = useState(() => new Set());
  const [accepting, setAccepting] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, s] = await Promise.all([
        fetchUpsBills(), fetchUpsBillsSummary(),
      ]);
      setBills(Array.isArray(b) ? b : []);
      setSummary(s || null);
    } catch (e) {
      setError(e.message || 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Upload ─────────────────────────────────────────────────────────────────
  // Two files make up a weekly bill: the Billing Data CSV (what we reconcile
  // against) and the PDF invoice (what staff actually read — shipment
  // grouping, message codes, summary of charges). Both are staged first so the
  // import can insist on the pair, with an explicit override for the weeks the
  // PDF genuinely isn't available.
  const stageFiles = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploadError(null);
    setUploadResult(null);
    const rejected = [];
    let csv = null, pdf = null;
    for (const f of list) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (ext === 'csv') csv = f;
      else if (ext === 'pdf') pdf = f;
      else rejected.push(f.name);
    }
    if (csv) setPendingCsv(csv);
    if (pdf) setPendingPdf(pdf);
    if (rejected.length) {
      setUploadError(`Not a CSV or PDF, ignored: ${rejected.join(', ')}`);
    }
  };

  const runImport = async () => {
    if (!pendingCsv) return;
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);
    try {
      const text = await pendingCsv.text();
      const res = await importUpsBill(text, pendingCsv.name);
      // A failed PDF upload must not read as a failed import — the bill is in.
      let pdfWarning = null, pdfAttached = false;
      if (pendingPdf && res?.invoice_number) {
        try { await uploadUpsBillPdf(res.invoice_number, pendingPdf); pdfAttached = true; }
        catch (e) { pdfWarning = `Bill imported, but the PDF didn't upload: ${e.message}`; }
      }
      setUploadResult({ ...res, pdfWarning, pdfAttached });
      setPendingCsv(null);
      setPendingPdf(null);
      await loadList();
      // Auto-kick backfill for any tracking #s whose orders aren't in Neon
      // yet (or are missing vatnumber / scale cost). The 15-min sync also
      // backfills these now, but kicking right at import means the user
      // doesn't have to wait for the next sync cycle to see clean recon.
      if (res?.invoice_number) {
        startUpsBillBackfill(res.invoice_number).catch(e =>
          console.warn('[ups-bills] auto-backfill kick failed:', e.message)
        );
      }
    } catch (e) {
      setUploadError(e.message || 'Import failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    stageFiles(e.dataTransfer.files);
  };

  // The PDF link is a signed, expiring URL, so it has to be fetched on click.
  // Open the tab synchronously first — resolving the URL and then calling
  // window.open trips the popup blocker.
  const viewBillPdf = async (key) => {
    const tab = window.open('', '_blank');
    try {
      const url = await upsBillPdfUrl(key);
      if (tab) tab.location = url; else window.location.href = url;
    } catch (e) {
      if (tab) tab.close();
      setUploadError(e.message || 'Could not open the PDF');
    }
  };

  // Attach (or replace) the PDF on a bill that's already imported — most of
  // the existing invoices came in CSV-only.
  const attachPdfToBill = async (invoiceNumber, file) => {
    if (!file) return;
    setPdfBusy(invoiceNumber);
    setUploadError(null);
    try {
      await uploadUpsBillPdf(invoiceNumber, file);
      await loadList();
    } catch (e) {
      setUploadError(`Could not attach the PDF to ${invoiceNumber}: ${e.message}`);
    } finally {
      setPdfBusy(null);
    }
  };

  // ── Detail / reconciliation ────────────────────────────────────────────────
  const loadExtras = useCallback(async (invNo) => {
    setExtrasLoading(true);
    try {
      const rows = await fetchUpsBillExtras(invNo);
      setExtras(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setExtras([]);
    } finally {
      setExtrasLoading(false);
    }
  }, []);

  const loadAccountCharges = useCallback(async (invNo) => {
    setAccountLoading(true);
    try {
      setAccountCharges(await fetchUpsBillAccountCharges(invNo));
    } catch (e) {
      setAccountCharges(null);
    } finally {
      setAccountLoading(false);
    }
  }, []);

  const loadRefunds = useCallback(async (invNo) => {
    setRefundLoading(true);
    try {
      const res = await fetchUpsRefunds(invNo);
      setRefunds(Array.isArray(res?.rows) ? res.rows : []);
    } catch (e) {
      setRefunds([]);
    } finally {
      setRefundLoading(false);
    }
  }, []);

  // Service Refund section = GSR credit lines (UPS already credited these for
  // prior-week service failures). Pulled from the charge lines by the "GSR" tag.
  const loadServiceRefunds = useCallback(async (invNo) => {
    setServiceRefundsLoading(true);
    try {
      const rows = await fetchUpsBillCharges(invNo, { search: 'GSR', limit: 1000 });
      const gsr = (Array.isArray(rows) ? rows : []).filter(l => /gsr/i.test(l.charge_description || ''));
      setServiceRefunds(gsr);
      // Link these credits back to refunds/claims we filed earlier.
      try {
        const credits = gsr.filter(l => l.tracking_number).map(l => ({ tracking_number: l.tracking_number, net: Number(l.net_amount || 0) }));
        const res = credits.length ? await matchUpsRefundCredits(credits) : { matches: [] };
        setCreditMatches(Array.isArray(res?.matches) ? res.matches : []);
      } catch (_) { setCreditMatches([]); }
    } catch (e) {
      setServiceRefunds([]);
      setCreditMatches([]);
    } finally {
      setServiceRefundsLoading(false);
    }
  }, []);

  const stopRefundPoll = () => { if (refundPollRef.current) { clearInterval(refundPollRef.current); refundPollRef.current = null; } };
  const runRefundScan = async () => {
    if (!activeInvoice) return;
    const inv = activeInvoice.invoice_number;
    setRefundScan({ status: 'running', checked: 0, same_day: 0, whole_day: 0 });
    try { await startUpsRefundScan(inv); } catch (e) { setRefundScan({ status: 'error', message: e.message }); return; }
    stopRefundPoll();
    refundPollRef.current = setInterval(async () => {
      try {
        const st = await fetchUpsRefundScanStatus(inv);
        if (st) setRefundScan(st);
        if (st && (st.status === 'done' || st.status === 'error')) { stopRefundPoll(); if (st.status === 'done') loadRefunds(inv); }
      } catch (_) {}
    }, 3000);
  };
  const updateRefundStatus = async (tracking, status, notes) => {
    if (!activeInvoice) return;
    try { await setUpsRefundStatus(activeInvoice.invoice_number, tracking, status, notes); await loadRefunds(activeInvoice.invoice_number); }
    catch (e) { setError(e.message); }
  };

  // Whole-day late: File Claim (default) vs Shipping Credit Only (GSR refund, no claim).
  // Optimistic update only — no reload on success, so the row switches without a flash.
  const setResolutionMode = async (tracking, mode) => {
    if (!activeInvoice) return;
    setRefunds(rs => rs.map(r => r.tracking_number === tracking ? { ...r, resolution_mode: mode } : r));
    try { await setUpsRefundResolution(activeInvoice.invoice_number, tracking, mode); }
    catch (e) { setError(e.message); loadRefunds(activeInvoice.invoice_number); }  // revert on failure
  };
  useEffect(() => stopRefundPoll, []);

  // Auto-detect refunded orders straight from production. After the Late
  // Packages list loads, check each whole-day row that has no reship and no
  // explicit resolution against Auth.net (OrderPaymentsByOrderId) — a
  // Refunded/Credited transaction means it's ready to file, so it drops out of
  // "Waiting". Runs in the background (throttled) so the list isn't blocked.
  const refundCheckedRef = useRef(new Set());
  useEffect(() => {
    const candidates = refunds.filter(r =>
      r.late_kind === 'whole_day' && !r.has_reship && !r.claim_number && r.order_id &&
      !['waiting', 'claim', 'credit'].includes(r.resolution_mode) &&
      !refundCheckedRef.current.has(String(r.order_id)));
    if (!candidates.length) return;
    let cancelled = false;
    (async () => {
      const found = [];
      for (let i = 0; i < candidates.length && !cancelled; i += 5) {
        const chunk = candidates.slice(i, i + 5);
        const ids = await Promise.all(chunk.map(async (r) => {
          const oid = String(r.order_id);
          refundCheckedRef.current.add(oid);  // don't re-check this order this session
          try { return paymentsShowRefund(await fetchOrderPaymentsRaw(oid)) ? oid : null; }
          catch (_) { return null; }
        }));
        ids.filter(Boolean).forEach(id => found.push(id));
      }
      if (!cancelled && found.length) setRefundOrderIds(prev => new Set([...prev, ...found]));
    })();
    return () => { cancelled = true; };
  }, [refunds]);

  const openInvoice = async (invoiceNumber, restoreTab) => {
    try { sessionStorage.setItem('upsBillActiveInvoice', invoiceNumber); } catch (_) {}
    setView('detail');
    setActiveInvoice(null);
    const VALID_TABS = ['reconcile', 'extras', 'service_refunds', 'account', 'refunds'];
    setDetailTab(VALID_TABS.includes(restoreTab) ? restoreTab : 'reconcile');
    setRecon([]);
    setExtras([]);
    setAccountCharges(null);
    setReconSummary({ byStatus: {}, total: null });
    setStatusFilter('');
    setSearchInput('');
    setSearch('');
    setPage(0);
    setExpanded(null);
    setExpandedExtra(null);
    try {
      const inv = await fetchUpsBillInvoice(invoiceNumber);
      setActiveInvoice(inv);
      setSummaryOfCharges(null);
      fetchUpsBillSummaryOfCharges(invoiceNumber).then(setSummaryOfCharges).catch(() => {});
      loadExtras(invoiceNumber);
      loadAccountCharges(invoiceNumber);
      loadRefunds(invoiceNumber);
      loadServiceRefunds(invoiceNumber);
      setRefundScan(null);
    } catch (e) {
      setError(e.message);
    }
  };

  // Reopen the bill ONLY on a genuine hard-refresh (first mount + reload). A nav
  // click into Bills remounts this component too, but there we want the list — so
  // clear the stale key and stay on the list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const firstBoot = !upsBillsBooted;
    upsBillsBooted = true;
    let saved = null;
    try { saved = sessionStorage.getItem('upsBillActiveInvoice'); } catch (_) {}
    if (firstBoot && isPageReload() && saved) {
      let savedTab = null;
      try { savedTab = sessionStorage.getItem('upsBillActiveTab'); } catch (_) {}
      openInvoice(saved, savedTab);       // hard-refresh inside a bill → reopen it on the same tab
    } else if (saved) {
      try { sessionStorage.removeItem('upsBillActiveInvoice'); } catch (_) {}  // nav into Bills → list
    }
  }, []);

  // Remember which detail tab you're on so a hard-refresh (Ctrl+Shift+R) lands
  // back on it (e.g. Late Packages) instead of resetting to Reconciliation.
  // Paired with the invoice restore above.
  useEffect(() => {
    try { if (view === 'detail') sessionStorage.setItem('upsBillActiveTab', detailTab); } catch (_) {}
  }, [view, detailTab]);

  // Monotonic request id — only the latest reconciliation response is applied.
  // Without this, fast typing in the search box let an earlier (broader) query
  // resolve AFTER the final one, so the grid "flashed" and landed on the wrong
  // record. (See the 2014824 search bug.)
  const reconReqRef = useRef(0);
  const loadRecon = useCallback(async () => {
    if (!activeInvoice) return;
    const reqId = ++reconReqRef.current;
    setReconLoading(true);
    try {
      const [rows, sum, exp] = await Promise.all([
        fetchUpsBillReconciliation(activeInvoice.invoice_number, {
          tolerance, status: statusFilter || undefined,
          search: search.trim() || undefined,
          limit: PAGE_SIZE, offset: page * PAGE_SIZE,
        }),
        fetchUpsBillReconSummary(activeInvoice.invoice_number, tolerance),
        fetchUpsBillExplainedSummary(activeInvoice.invoice_number).catch(() => null),
      ]);
      if (reqId !== reconReqRef.current) return;  // a newer query superseded this one
      setRecon(Array.isArray(rows) ? rows : []);
      setTotalRows(rows && rows.length ? Number(rows[0].total_rows) || 0 : 0);
      setReconSummary(sum || { byStatus: {}, total: null });
      setExplained(exp || null);
    } catch (e) {
      if (reqId === reconReqRef.current) setError(e.message);
    } finally {
      if (reqId === reconReqRef.current) setReconLoading(false);
    }
  }, [activeInvoice, tolerance, statusFilter, search, page]);

  useEffect(() => { if (view === 'detail' && activeInvoice) loadRecon(); }, [view, activeInvoice, loadRecon]);

  // Debounce the search box so we fire one query after typing settles, not one
  // per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPoll, []);

  const runBackfill = async () => {
    if (!activeInvoice || backfilling) return;
    const inv = activeInvoice.invoice_number;
    setBackfilling(true);
    setBackfillResult({ status: 'running' });
    try {
      await startUpsBillBackfill(inv);
    } catch (e) {
      setBackfillResult({ status: 'error', message: e.message || 'Backfill could not start' });
      setBackfilling(false);
      return;
    }
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const st = await fetchUpsBackfillStatus(inv);
        if (st) setBackfillResult(st);
        if (st && (st.status === 'done' || st.status === 'error')) {
          stopPoll();
          setBackfilling(false);
          if (st.status === 'done') { loadRecon(); loadExtras(inv); }
        }
      } catch (e) { /* transient — keep polling */ }
    }, 4000);
  };

  // ── Accept UPS-billed charge as-is (mirrors legacy auto-reconcile) ────────
  // Only "Cost off" (variance) + "No cost" rows are acceptable. Late stays on
  // the File Claim path; no_data needs Backfill first; already-OK doesn't need
  // accepting. We don't touch orders.ship_our_cost — the accept log overrides
  // the recon view via LEFT JOIN, so the original scale value stays auditable.
  const canAcceptRow = (r) => !r.is_accepted && (r.status === 'variance' || r.status === 'no_cost');

  const acceptRows = async (rows) => {
    if (!activeInvoice || accepting || !rows.length) return;
    const inv = activeInvoice.invoice_number;
    const items = rows.map(r => ({
      tracking_number: r.tracking_number,
      accepted_amount: Number(r.billed_core),
      prior_scale_cost: r.scale_cost == null ? null : Number(r.scale_cost),
      variance: r.variance == null ? null : Number(r.variance),
      order_id: r.order_id || null,
    }));
    setAccepting(true);
    try {
      const u = getCurrentUser();
      const acceptedBy = u?.email || u?.staff_name || null;
      await acceptUpsBillCharges(inv, items, { accepted_by: acceptedBy });
      setSelected(new Set());
      await Promise.all([loadRecon(), /* keep extras tab in sync if open */]);
    } catch (e) {
      setError(e.message || 'Failed to accept charges');
    } finally {
      setAccepting(false);
    }
  };

  // Bulk-accept the "explained" Cost-off rows (SIM contract credits or $0.01
  // rounding) in one click — clears them out of Cost off at every tolerance.
  const bulkAccept = async (kind) => {
    if (!activeInvoice || bulkAccepting) return;
    setBulkAccepting(true);
    try {
      const u = getCurrentUser();
      await bulkAcceptUpsBillCharges(activeInvoice.invoice_number, kind, u?.email || u?.staff_name || null);
      await loadRecon();
    } catch (e) {
      setError(e.message || 'Bulk accept failed');
    } finally {
      setBulkAccepting(false);
    }
  };

  const undoAccept = async (tracking) => {
    if (!activeInvoice || accepting || !tracking) return;
    setAccepting(true);
    try {
      await unacceptUpsBillCharges(activeInvoice.invoice_number, [tracking]);
      await loadRecon();
    } catch (e) {
      setError(e.message || 'Failed to undo accept');
    } finally {
      setAccepting(false);
    }
  };

  // Selection helpers — checkboxes only render for rows where canAcceptRow().
  const toggleSelected = (tracking) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tracking)) next.delete(tracking);
      else next.add(tracking);
      return next;
    });
  };
  const selectableOnPage = recon.filter(canAcceptRow).map(r => r.tracking_number);
  const allSelectedOnPage = selectableOnPage.length > 0 && selectableOnPage.every(t => selected.has(t));
  const someSelectedOnPage = selectableOnPage.some(t => selected.has(t));
  const togglePageSelection = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelectedOnPage) selectableOnPage.forEach(t => next.delete(t));
      else selectableOnPage.forEach(t => next.add(t));
      return next;
    });
  };

  // Clear selection when filters/page/invoice change — stale checkboxes are
  // confusing since the rows they reference may no longer be on screen.
  useEffect(() => { setSelected(new Set()); },
    [activeInvoice?.invoice_number, statusFilter, search, page, tolerance]);

  const toggleExpand = async (tracking) => {
    if (expanded === tracking) { setExpanded(null); return; }
    setExpanded(tracking);
    setExpandedLines([]);
    setExpandedLoading(true);
    try {
      const lines = await fetchUpsBillCharges(activeInvoice.invoice_number, { tracking, limit: 500 });
      setExpandedLines(Array.isArray(lines) ? lines : []);
    } catch (e) {
      setExpandedLines([]);
    } finally {
      setExpandedLoading(false);
    }
  };

  // Open the File-Claim modal in place — no navigation to the Claims page, so
  // the prefill is passed straight through (the Claims page's prefill handler
  // reshapes for the Order Details order shape and would drop these fields).
  const fileClaim = (r) => {
    setClaimModal({
      order_id: r.order_id, bc_order_id: r.bc_order_id,
      tracking_number: r.tracking_number,
      selected_delivery: r.delivery_date,
      actual_delivery: r.actual_delivery_date,
      shipping_cost: r.scale_cost,
      // Merchandise amount is filled from the order's line items in the modal —
      // r.billed_net is the UPS shipping charge, not the value of the goods.
      customer_name: r.order_customer || r.receiver_name,
      // UPS billing-data recipient — the bill carries the delivery
      // ZIP/city/state even when the matched order row has no address.
      receiver_name: r.receiver_name,
      receiver_city: r.receiver_city,
      receiver_state: r.receiver_state,
      receiver_zip: r.receiver_zip,
    });
  };

  // File a claim from a Late-Packages (ups_refunds) row — same modal, fields
  // mapped from the refund row shape.
  const fileClaimFromRefund = (r) => {
    setClaimModal({
      order_id: r.order_id, bc_order_id: r.bc_order_id,
      tracking_number: r.tracking_number,
      selected_delivery: r.scheduled_date,
      actual_delivery: r.delivered_at ? String(r.delivered_at).slice(0, 10) : null,
      shipping_cost: r.refund_amount,
      customer_name: r.customer,
      receiver_name: r.customer,
      receiver_state: r.state,
      receiver_zip: r.zip,
    });
  };

  // ── Render: list ───────────────────────────────────────────────────────────
  const renderList = () => (
    <>
      {/* Add a new invoice — UPS doesn't email these, so each week pull the
          Billing Data CSV from the UPS Billing Center, then drop it below. */}
      <div className="card" style={{ marginBottom: 12, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--navy)' }}>Add a new invoice</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>
              UPS doesn't email these. Each week: open the UPS Billing Center, download the{' '}
              <strong>Billing Data</strong> file (the CSV, not the PDF) for the new invoice, then drop it below.
            </div>
          </div>
          <a href="https://billing.ups.com/ups/billing/invoice" target="_blank" rel="noopener noreferrer"
            className="btn btn-primary" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FileText size={15} /> Get the CSV from UPS ↗
          </a>
        </div>
      </div>

      <div
        className="card"
        style={{
          marginBottom: 16, padding: 20, border: dragOver ? '2px dashed var(--orange)' : '2px dashed var(--gray-300)',
          background: dragOver ? 'var(--peach)' : undefined, textAlign: 'center', cursor: 'pointer',
        }}
        onClick={() => fileRef.current && fileRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <input
          ref={fileRef} type="file" accept=".csv,text/csv,.pdf,application/pdf" multiple style={{ display: 'none' }}
          onChange={(e) => stageFiles(e.target.files)}
        />
        <Upload size={28} style={{ color: 'var(--orange)', marginBottom: 8 }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>
          {uploading ? 'Importing…' : 'Import this week’s UPS bill'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          Drop <strong>both</strong> files here (or click to browse): the <strong>Billing Data CSV</strong>,
          which is what gets reconciled, and the <strong>PDF invoice</strong>, which is what you read.
          Re-importing an invoice replaces it.
        </div>

        {/* Staged files — one slot each, so it's obvious what's still missing. */}
        <div
          style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}
          onClick={(e) => e.stopPropagation()}
        >
          {[
            { key: 'csv', label: 'Billing Data CSV', file: pendingCsv, clear: () => setPendingCsv(null), required: true },
            { key: 'pdf', label: 'PDF invoice', file: pendingPdf, clear: () => setPendingPdf(null), required: false },
          ].map(slot => (
            <div key={slot.key} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6,
              minWidth: 240, textAlign: 'left', fontSize: 12,
              border: `1px solid ${slot.file ? '#1a8a4a' : 'var(--gray-300)'}`,
              background: slot.file ? '#e6f4ea' : 'var(--gray-50)',
            }}>
              {slot.file
                ? <CheckCircle size={15} style={{ color: '#1a8a4a', flexShrink: 0 }} />
                : <FileText size={15} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{slot.label}</div>
                <div style={{
                  color: slot.file ? 'var(--gray-600)' : 'var(--gray-400)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {slot.file ? slot.file.name : (slot.required ? 'Required' : 'Not added yet')}
                </div>
              </div>
              {slot.file && (
                <button type="button" className="btn btn-ghost" title="Remove"
                  style={{ padding: 2, lineHeight: 0 }} onClick={slot.clear}>
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Gate: both files -> straight through. CSV only -> nudge + explicit
            override, because a missing PDF is usually forgetfulness, not a
            week UPS didn't send one. */}
        {pendingCsv && (
          <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
            {pendingPdf ? (
              <button type="button" className="btn btn-primary" disabled={uploading} onClick={runImport}>
                {uploading ? 'Importing…' : 'Import bill + PDF'}
              </button>
            ) : (
              <>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12,
                  padding: '8px 14px', borderRadius: 6, background: '#fff8e1', color: '#8a6d1a',
                  border: '1px solid #f0d999', marginBottom: 10,
                }}>
                  <AlertTriangle size={15} />
                  <span>
                    The <strong>PDF invoice</strong> is missing. Add it too — it's the copy you'll
                    want when a charge needs explaining.
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-primary"
                    onClick={() => fileRef.current && fileRef.current.click()}>
                    Add the PDF
                  </button>
                  <button type="button" className="btn btn-ghost" disabled={uploading}
                    style={{ fontSize: 12, color: 'var(--gray-500)', textDecoration: 'underline' }}
                    onClick={runImport}>
                    {uploading ? 'Importing…' : 'Import CSV only — I don’t have the PDF'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {!pendingCsv && pendingPdf && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#8a6d1a' }} onClick={(e) => e.stopPropagation()}>
            <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} />{' '}
            Add the <strong>Billing Data CSV</strong> as well — the PDF on its own can't be reconciled.
          </div>
        )}
        {uploadResult && (
          <div style={{
            marginTop: 12, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 6,
            background: uploadResult.ties_out ? '#e6f4ea' : '#fde8e8',
            color: uploadResult.ties_out ? '#1a8a4a' : '#c62828',
          }}>
            {uploadResult.ties_out ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
            Imported invoice <strong>{uploadResult.invoice_number}</strong> — {uploadResult.line_count} lines,
            {' '}{uploadResult.package_count} packages, {fmt(uploadResult.charge_total)}
            {uploadResult.ties_out ? ' (ties out)' : ` (≠ invoice total ${fmt(uploadResult.invoice_amount)})`}
            {uploadResult.pdfAttached ? ' · PDF attached' : ''}
          </div>
        )}
        {uploadResult?.pdfWarning && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#8a6d1a' }}>
            <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> {uploadResult.pdfWarning}
          </div>
        )}
        {uploadError && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> {uploadError}
          </div>
        )}
      </div>

      {/* Shared picker for the per-invoice "+ Add" PDF buttons in the list. */}
      <input
        ref={attachRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          const target = attachTargetRef.current;
          e.target.value = '';
          if (f && target) attachPdfToBill(target, f);
        }}
      />


      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Invoices', value: summary.invoice_count || 0, icon: FileText, color: 'var(--navy)' },
            { label: 'Total Billed', value: fmt(summary.total_billed), icon: DollarSign, color: 'var(--navy)' },
            { label: 'Packages', value: Number(summary.total_packages || 0).toLocaleString(), icon: Package, color: 'var(--navy)' },
            { label: 'Charge Lines', value: Number(summary.total_lines || 0).toLocaleString(), icon: FileText, color: 'var(--gray-500)' },
            { label: 'Not Tied Out', value: summary.not_tied_out || 0, icon: AlertTriangle, color: Number(summary.not_tied_out) > 0 ? 'var(--red)' : '#1a8a4a' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="card" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Icon size={14} style={{ color: 'var(--gray-400)' }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Imported Invoices — click to reconcile</span>
          <button onClick={loadList} className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }}>
            <RefreshCw size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Refresh
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>
          ) : bills.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
              No invoices imported yet. Drop a UPS Billing Data file above to get started.
            </div>
          ) : (
            <table className="product-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Invoice #</th>
                  <th style={{ textAlign: 'left' }}>Invoice Date</th>
                  <th style={{ textAlign: 'left' }}>Due Date</th>
                  <th style={{ textAlign: 'left' }}>Account</th>
                  <th style={{ textAlign: 'right' }}>Packages</th>
                  <th style={{ textAlign: 'right' }}>Transport</th>
                  <th style={{ textAlign: 'right' }}>Fuel</th>
                  <th style={{ textAlign: 'right' }}>Accessorial</th>
                  <th style={{ textAlign: 'right' }}>Net Total</th>
                  <th style={{ textAlign: 'center' }}>Tie-Out</th>
                  <th style={{ textAlign: 'center' }}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <tr key={b.invoice_number} onClick={() => openInvoice(b.invoice_number)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 700, color: 'var(--orange)' }}>{b.invoice_number}</td>
                    <td>{fmtDate(b.invoice_date)}</td>
                    <td>{fmtDate(b.due_date)}</td>
                    <td>{b.account_number}</td>
                    <td style={{ textAlign: 'right' }}>{Number(b.package_count || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.cat_frt)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.cat_fsc)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.cat_acc)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(b.charge_total)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {b.ties_out === false ? (
                        <span style={{ color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                          <AlertTriangle size={13} /> Off
                        </span>
                      ) : (
                        <span style={{ color: '#1a8a4a', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                          <CheckCircle size={13} /> OK
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {b.pdf_key ? (
                        <button type="button" className="btn btn-ghost"
                          title={b.pdf_filename || 'View the PDF invoice'}
                          style={{ color: 'var(--orange)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px' }}
                          onClick={() => viewBillPdf(b.pdf_key)}>
                          <FileText size={13} /> View
                        </button>
                      ) : (
                        <button type="button" className="btn btn-ghost"
                          disabled={pdfBusy === b.invoice_number}
                          style={{ fontSize: 11, color: 'var(--gray-500)', padding: '2px 6px' }}
                          title="This bill was imported without its PDF invoice — add it now"
                          onClick={() => {
                            attachTargetRef.current = b.invoice_number;
                            if (attachRef.current) attachRef.current.click();
                          }}>
                          {pdfBusy === b.invoice_number ? 'Uploading…' : '+ Add'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );

  // ── Render: reconciliation tab ─────────────────────────────────────────────
  const renderReconcileTab = () => {
    const pages = Math.ceil(totalRows / PAGE_SIZE) || 1;
    const total = reconSummary.total;
    const okN = reconSummary.byStatus.ok ? reconSummary.byStatus.ok.n : 0;
    const totalN = total ? total.n : 0;
    const attention = Math.max(0, totalN - okN);
    const noData = (reconSummary.byStatus.no_data && reconSummary.byStatus.no_data.n) || 0;

    return (
      <>
        {/* Attention headline + tolerance */}
        <div className="card" style={{ marginBottom: 14, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 700 }}>
            {totalN > 0
              ? <>{okN} of {totalN} packages clean —{' '}
                  <span style={{ color: attention > 0 ? 'var(--red)' : '#1a8a4a' }}>{attention} need attention</span>
                  <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}> · "clean" = core charges match the scale</span></>
              : 'No packages to reconcile'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Scale size={13} style={{ color: 'var(--gray-400)' }} />
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Match tolerance:</span>
            {TOL_PRESETS.map(t => (
              <button
                key={t.label}
                onClick={() => { setTolerance(t.v); setPage(0); }}
                className={tolerance === t.v ? 'btn btn-primary' : 'btn btn-outline'}
                style={{ fontSize: 11, padding: '3px 9px' }}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {/* Bulk-accept the "explained" Cost-off rows (SIM credits / $0.01 rounding) */}
        {explained && (Number(explained.sim_acceptable) > 0 || Number(explained.rounding_acceptable) > 0) && (
          <div className="card" style={{ marginBottom: 14, padding: '10px 16px', background: '#f0f7f2', borderLeft: '3px solid #1a8a4a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--navy)', maxWidth: 640 }}>
              <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6, color: '#1a8a4a' }} />
              <strong>{Number(explained.sim_acceptable) + Number(explained.rounding_acceptable)} Cost-off rows are explained</strong> — the
              difference is a contract <strong>SIM credit</strong> and/or a <strong>$0.01 rounding</strong>, not a real discrepancy. Accept to clear them.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Number(explained.rounding_acceptable) > 0 && (
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 12px' }} disabled={bulkAccepting}
                  onClick={() => bulkAccept('rounding')}>
                  <Check size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Accept {explained.rounding_acceptable} rounding ({fmtSigned(explained.rounding_acceptable_amt)})
                </button>
              )}
              {Number(explained.sim_acceptable) > 0 && (
                <button className="btn btn-primary" style={{ fontSize: 11, padding: '4px 12px' }} disabled={bulkAccepting}
                  onClick={() => bulkAccept('sim')}>
                  <Check size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Accept {explained.sim_acceptable} SIM credit{Number(explained.sim_acceptable) === 1 ? '' : 's'} ({fmtSigned(explained.sim_acceptable_amt)})
                </button>
              )}
            </div>
          </div>
        )}

        {/* "No order" explainer + backfill */}
        {(noData > 0 || backfillResult) && (
          <div className="card" style={{ marginBottom: 14, padding: '12px 16px', background: '#eef4fb', borderLeft: '3px solid var(--navy)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: 'var(--navy)', maxWidth: 760 }}>
                <Info size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                <strong>{noData} packages show "No order."</strong>{' '}
                UPS billed these but the order isn't in our database yet — it only syncs recent
                orders, so a package whose order was placed weeks before it shipped never got
                copied over. Backfill pulls the missing orders + scale cost from the live system,
                so Order, Scale, Δ and Late fill in.
              </div>
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
                disabled={backfilling} onClick={runBackfill}>
                <DownloadCloud size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {backfilling ? 'Backfilling…' : 'Backfill missing orders'}
              </button>
            </div>
            {backfillResult && (
              <div style={{ marginTop: 10, fontSize: 12, color: backfillResult.status === 'error' ? 'var(--red)' : backfillResult.status === 'done' ? '#1a8a4a' : 'var(--navy)' }}>
                {backfillResult.status === 'error' ? (
                  <><AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> {backfillResult.message || 'Backfill failed'}</>
                ) : backfillResult.status === 'done' ? (
                  <><CheckCircle size={13} style={{ verticalAlign: 'middle' }} /> Resolved {backfillResult.tier1_resolved || 0} orders
                    {' '}({backfillResult.tier2_cost_filled || 0} with scale cost) · {backfillResult.still_unmatched ?? 0} still not in the live system.</>
                ) : (
                  <><RefreshCw size={13} style={{ verticalAlign: 'middle' }} /> Working in the background — resolved {backfillResult.tier1_resolved || 0} so far
                    {backfillResult.tier2_cost_filled ? `, ${backfillResult.tier2_cost_filled} with scale cost` : ''}… (safe to keep using the page)</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary panes */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { key: '', label: 'All', color: 'var(--navy)', bg: '#fff', n: totalN, amt: total ? total.core_total : 0, blurb: 'All packages — core billed' },
            ...STATUS_ORDER.map(k => {
              const s = reconSummary.byStatus[k] || { n: 0, core_total: 0, variance_total: 0 };
              // Cost Off shows the net $ it's off (sum of Δ), not the core billed total.
              const isVar = k === 'variance';
              return {
                key: k, label: STATUS[k].label, color: STATUS[k].color, bg: STATUS[k].bg, n: s.n,
                amt: isVar ? s.variance_total : s.core_total, signed: isVar,
                blurb: isVar ? 'Net $ the core charges are off vs the scale' : STATUS[k].blurb,
              };
            }),
          ].map(c => (
            <div
              key={c.key || 'all'}
              onClick={() => { setStatusFilter(c.key); setPage(0); }}
              className="card"
              style={{
                padding: '10px 12px', cursor: 'pointer',
                outline: statusFilter === c.key ? `2px solid ${c.color}` : 'none',
                background: c.bg,
              }}
              title={c.blurb}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.n}</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.signed ? fmtSigned(c.amt) : fmt(c.amt)}</div>
            </div>
          ))}
          {/* SIM pane → filters the grid to packages carrying a contract SIM credit */}
          <div
            onClick={() => { setStatusFilter('sim'); setPage(0); }}
            className="card"
            style={{ padding: '10px 12px', cursor: 'pointer', outline: statusFilter === 'sim' ? '2px solid #6b3fa0' : 'none', background: '#f6f2fb' }}
            title="Packages with a contract Special-Incentive (SIM) credit"
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b3fa0', textTransform: 'uppercase', letterSpacing: '0.4px' }}>SIM</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#6b3fa0' }}>{total ? (total.sim_n || 0) : 0}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{fmtSigned(total ? (total.sim_total || 0) : 0)}</div>
          </div>
          {/* Extras pane → jumps to the Extras tab */}
          <div
            onClick={() => setDetailTab('extras')}
            className="card"
            style={{ padding: '10px 12px', cursor: 'pointer', background: '#fff1e8' }}
            title="Adjustments / corrections / re-rates — not in the scale estimate. Opens the Extras tab."
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Extras ▸</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orange)' }}>{total ? (total.extra_pkgs || 0) : 0}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{fmt(total ? total.extra_total : 0)}</div>
          </div>
          {/* Account-fees pane → jumps to the Account Charges tab. These are
              billed to the account, not a package, so they never show in the
              per-package buckets above — this is where they live. */}
          <div
            onClick={() => setDetailTab('account')}
            className="card"
            style={{ padding: '10px 12px', cursor: 'pointer', background: '#f3eefc' }}
            title="Fees billed to the account, not to any package (weekly pickup/service fees, account-wide adjustments). Opens the Account Charges tab."
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b3fa0', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Account fees ▸</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#6b3fa0' }}>{accountCharges ? (accountCharges.account_line_count || 0) : 0}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{fmt(accountCharges ? accountCharges.account_total : 0)}</div>
          </div>
        </div>

        {/* Table */}
        <div className="card recon-card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>
              By tracking number — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>Billed = UPS package cost (vs scale); Extra = adjustments. Late = whole-day (red d) or same-day (red m).</span>
              {statusFilter && <> · filtered to <strong style={{ color: statusFilter === 'sim' ? '#6b3fa0' : STATUS[statusFilter]?.color }}>{statusFilter === 'sim' ? 'SIM' : STATUS[statusFilter]?.label}</strong></>}
            </span>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 8, top: 7, color: 'var(--gray-400)' }} />
              <input
                className="form-input"
                placeholder="Tracking #, order #, customer…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ fontSize: 12, padding: '5px 8px 5px 26px', width: 250 }}
              />
              {searchInput && (
                <X size={13} onClick={() => setSearchInput('')}
                  style={{ position: 'absolute', right: 8, top: 7, color: 'var(--gray-400)', cursor: 'pointer' }} />
              )}
            </div>
          </div>
          {selected.size > 0 && (
            <div style={{
              padding: '8px 16px', background: '#fff8e1', borderTop: '1px solid #f0d56a',
              borderBottom: '1px solid #f0d56a', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 12, fontSize: 12,
            }}>
              <div style={{ color: '#7a5b00' }}>
                <strong>{selected.size}</strong> selected ·{' '}
                {(() => {
                  const sel = recon.filter(r => selected.has(r.tracking_number));
                  const net = sel.reduce((acc, r) => acc + Number(r.variance || 0), 0);
                  return <>net Δ <strong>{fmtSigned(net)}</strong></>;
                })()}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => setSelected(new Set())} disabled={accepting}>
                  Clear
                </button>
                <button className="btn btn-primary" style={{ fontSize: 11, padding: '4px 12px' }}
                  disabled={accepting}
                  onClick={() => acceptRows(recon.filter(r => selected.has(r.tracking_number) && canAcceptRow(r)))}>
                  <Check size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {accepting ? 'Accepting…' : `Accept ${selected.size} UPS charge${selected.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          )}
          <div className="card-body" style={{ padding: 0 }}>
            {reconLoading ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Reconciling…</div>
            ) : recon.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>No packages match.</div>
            ) : (
              <table className="product-table recon-sticky" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 28, textAlign: 'center' }}>
                      {selectableOnPage.length > 0 && (
                        <input
                          type="checkbox"
                          aria-label="Select all acceptable rows on this page"
                          title={allSelectedOnPage ? 'Clear page selection' : 'Select all acceptable rows on this page'}
                          checked={allSelectedOnPage}
                          ref={el => { if (el) el.indeterminate = !allSelectedOnPage && someSelectedOnPage; }}
                          onChange={togglePageSelection}
                        />
                      )}
                    </th>
                    <th style={{ width: 24 }}></th>
                    <th style={{ textAlign: 'left' }}>Status</th>
                    <th style={{ textAlign: 'left' }}>Tracking #</th>
                    <th style={{ textAlign: 'left' }}>Order</th>
                    <th style={{ textAlign: 'left' }}>Customer</th>
                    <th style={{ textAlign: 'left' }}>Service</th>
                    <th style={{ textAlign: 'right' }}>Scale</th>
                    <th style={{ textAlign: 'right' }}>Billed</th>
                    <th style={{ textAlign: 'right' }}>Δ</th>
                    <th style={{ textAlign: 'right' }}>Extra</th>
                    <th style={{ textAlign: 'left' }}>Scheduled</th>
                    <th style={{ textAlign: 'left' }}>Delivered</th>
                    <th style={{ textAlign: 'center' }}>Late</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recon.map((r) => {
                    const meta = STATUS[r.status] || STATUS.no_data;
                    const Icon = meta.icon;
                    const isOpen = expanded === r.tracking_number;
                    const vNum = r.variance == null ? null : Number(r.variance);
                    const extra = Number(r.adjustment_amt || 0);
                    const acceptable = canAcceptRow(r);
                    const isChecked = selected.has(r.tracking_number);
                    const sdMin = Number(r.same_day_minutes) || 0;  // same-day late minutes (server)
                    return (
                      <React.Fragment key={r.tracking_number}>
                        <tr style={{ background: r.status === 'ok' ? (r.is_accepted ? '#f3faf6' : undefined) : meta.bg }}>
                          <td style={{ textAlign: 'center' }}>
                            {acceptable && (
                              <input
                                type="checkbox"
                                aria-label={`Select ${r.tracking_number}`}
                                checked={isChecked}
                                onChange={() => toggleSelected(r.tracking_number)}
                              />
                            )}
                          </td>
                          <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleExpand(r.tracking_number)}>
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </td>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: meta.color, fontWeight: 700, fontSize: 11 }}>
                              <Icon size={13} /> {r.status === 'late' ? (sdMin > 0 ? 'Same-Day Late' : 'Whole-Day Late') : meta.label}
                            </span>
                            {r.is_accepted && (
                              <span title={`Accepted${r.accepted_by ? ` by ${r.accepted_by}` : ''}${r.accepted_at ? ` on ${fmtDate(r.accepted_at)}` : ''}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#1a8a4a', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                <Check size={10} /> Accepted
                              </span>
                            )}
                          </td>
                          <td style={{ fontFamily: 'monospace' }}>{r.tracking_number}</td>
                          <td><OrderNo row={r} /></td>
                          <td>
                            {r.order_customer || r.receiver_name || '—'}
                            {r.receiver_state ? <span style={{ color: 'var(--gray-400)' }}> · {r.receiver_state}</span> : null}
                            {/* With no order behind it, the ship-to on the bill is
                                the only thing identifying who got the box. */}
                            {r.no_order && r.receiver_address && (
                              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                                {r.receiver_address}{r.receiver_address2 ? `, ${r.receiver_address2}` : ''}
                                {r.receiver_city ? `, ${r.receiver_city}` : ''}
                              </div>
                            )}
                          </td>
                          <td>{r.order_service || r.service_desc || '—'}
                            {r.receiver_zip && <span style={{ color: 'var(--gray-400)' }}> ({String(r.receiver_zip).replace(/\D/g, '').slice(0, 5)})</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>{r.scale_cost == null ? '—' : fmt(r.scale_cost)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.billed_core)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: vNum == null ? 'var(--gray-400)' : Math.abs(vNum) > Number(tolerance) ? (vNum > 0 ? 'var(--red)' : '#1a8a4a') : 'var(--gray-500)' }}>
                            {vNum == null ? '—' : fmtSigned(vNum)}
                          </td>
                          <td style={{ textAlign: 'right', color: extra > 0.005 ? 'var(--orange)' : extra < -0.005 ? '#1a8a4a' : 'var(--gray-400)' }}>
                            {Math.abs(extra) < 0.005 ? '—' : fmtSigned(extra)}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', color: 'var(--gray-600)' }}>
                            {fmtDate(r.delivery_date)}
                            {r.committed_time && <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>by {r.committed_time}</div>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', color: r.actual_delivery_date ? 'var(--navy)' : 'var(--gray-400)', fontWeight: r.actual_delivery_date ? 600 : 400 }}>
                            {fmtDate(r.actual_delivery_date)}
                            {fmtClock(r.delivered_ts) && <div style={{ fontSize: 10, color: sdMin > 0 ? 'var(--red)' : 'var(--gray-400)', fontWeight: 400 }}>{fmtClock(r.delivered_ts)}</div>}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {Number(r.days_late) > 0
                              ? <span style={{ color: 'var(--red)', fontWeight: 700 }} title="Whole-day late">{r.days_late}d</span>
                              : sdMin > 0
                                ? <span style={{ color: 'var(--red)', fontWeight: 700 }} title="Same-day late (delivered after the committed time)">{sdMin}m</span>
                                : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {acceptable && (
                              <button className="btn btn-outline" style={{ fontSize: 10, padding: '2px 7px' }}
                                disabled={accepting}
                                title="Accept the UPS billed amount as the scale cost — clears the variance"
                                onClick={() => acceptRows([r])}>
                                <Check size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                                Accept
                              </button>
                            )}
                            {r.is_accepted && (
                              <button className="btn btn-outline" style={{ fontSize: 10, padding: '2px 7px', color: 'var(--gray-500)' }}
                                disabled={accepting}
                                title="Undo accept — variance comes back"
                                onClick={() => undoAccept(r.tracking_number)}>
                                <Undo2 size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                                Undo
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={15} style={{ background: '#fafbfc', padding: 0 }}>
                              {expandedLoading ? (
                                <div style={{ padding: 14, fontSize: 12, color: 'var(--gray-500)' }}>Loading charge lines…</div>
                              ) : (() => {
                                // Published = the UPS list/gross rate. UPS reports the incentive
                                // (the $ discount knocked off) + net; published = incentive + net
                                // (the published_charge column is blank in the Billing Data file).
                                const pub = l => Number(l.incentive_amount || 0) + Number(l.net_amount || 0);
                                const inc = l => Number(l.incentive_amount || 0);
                                const tPub = expandedLines.reduce((s, l) => s + pub(l), 0);
                                const tInc = expandedLines.reduce((s, l) => s + inc(l), 0);
                                const tNet = expandedLines.reduce((s, l) => s + Number(l.net_amount || 0), 0);
                                const scale = r.scale_cost == null ? null : Number(r.scale_cost);
                                const delta = scale == null ? null : Math.round((tNet - scale) * 100) / 100;
                                return (
                                  <div>
                                    {/* Scale (our estimate at the box) vs what UPS actually billed */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', padding: '10px 14px', background: '#eef4fb', borderBottom: '1px solid var(--gray-200)', fontSize: 11.5 }}>
                                      <span style={{ color: 'var(--gray-500)' }}>Scale (our estimate at the box):{' '}
                                        <strong style={{ color: 'var(--navy)' }}>{scale == null ? '—' : fmt(scale)}</strong></span>
                                      <span style={{ color: 'var(--gray-400)' }}>vs</span>
                                      <span style={{ color: 'var(--gray-500)' }}>UPS billed (this package):{' '}
                                        <strong style={{ color: 'var(--navy)' }}>{fmt(tNet)}</strong></span>
                                      {delta != null && (
                                        <span style={{ fontWeight: 700, color: Math.abs(delta) < 0.005 ? '#1a8a4a' : delta > 0 ? 'var(--red)' : '#1a8a4a' }}>
                                          Δ {fmtSigned(delta)}{delta > 0.005 ? ' — UPS billed more than the scale' : delta < -0.005 ? ' — UPS billed less' : ' — matches'}
                                        </span>
                                      )}
                                      <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>
                                        The scale stored one estimated total; UPS bills it itemized below — compare to see which line moved it.
                                      </span>
                                    </div>
                                    <table className="product-table" style={{ width: '100%', fontSize: 11, margin: 0 }}>
                                      <thead>
                                        <tr>
                                          <th style={{ textAlign: 'left' }}>Date</th>
                                          <th style={{ textAlign: 'left' }}>Category</th>
                                          <th style={{ textAlign: 'left' }}>Class</th>
                                          <th style={{ textAlign: 'left' }}>Description</th>
                                          <th style={{ textAlign: 'right' }} title="UPS list/gross rate (= incentive + net)">Published</th>
                                          <th style={{ textAlign: 'right' }} title="The discount UPS knocked off the published rate">Incentive</th>
                                          <th style={{ textAlign: 'right' }} title="What UPS actually billed">Net</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {expandedLines.map((l) => (
                                          <tr key={l.id}>
                                            <td>{fmtDate(l.transaction_date)}</td>
                                            <td>
                                              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: '#fff', background: CAT_COLOR[l.charge_category] || 'var(--gray-400)' }}>
                                                {l.charge_category}
                                              </span>
                                            </td>
                                            <td>{l.charge_class_code}</td>
                                            <td>{l.charge_description}</td>
                                            <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{fmt(pub(l))}</td>
                                            <td style={{ textAlign: 'right', color: inc(l) > 0.005 ? '#1a8a4a' : 'var(--gray-400)' }}>{inc(l) > 0.005 ? '−' + fmt(inc(l)) : '—'}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(l.net_amount)}</td>
                                          </tr>
                                        ))}
                                        {expandedLines.length === 0 && (
                                          <tr><td colSpan={7} style={{ padding: 12, color: 'var(--gray-500)' }}>No charge lines.</td></tr>
                                        )}
                                      </tbody>
                                      {expandedLines.length > 0 && (
                                        <tfoot>
                                          <tr style={{ borderTop: '2px solid var(--gray-200)', fontWeight: 700 }}>
                                            <td colSpan={4} style={{ textAlign: 'right', color: 'var(--navy)' }}>Totals</td>
                                            <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{fmt(tPub)}</td>
                                            <td style={{ textAlign: 'right', color: '#1a8a4a' }}>{tInc > 0.005 ? '−' + fmt(tInc) : '—'}</td>
                                            <td style={{ textAlign: 'right', color: 'var(--navy)' }}>{fmt(tNet)}</td>
                                          </tr>
                                        </tfoot>
                                      )}
                                    </table>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {totalRows > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', fontSize: 12 }}>
              <span style={{ color: 'var(--gray-500)' }}>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalRows)} of {totalRows.toLocaleString()}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                  disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                  <ChevronLeft size={13} /> Prev
                </button>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                  disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>
                  Next <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Render: extras tab ─────────────────────────────────────────────────────
  const renderExtrasTab = () => {
    const extraTotal = extras.length ? Number(extras[0].extra_total) || 0 : 0;

    // Group adjustment lines by tracking number — one row per package, like
    // the main tab. An address correction lands as a freight line + its
    // matching fuel-surcharge line; both belong to the same package.
    const byTn = new Map();
    const groups = [];
    for (const x of extras) {
      const k = x.tracking_number || `__${x.id}`;
      let g = byTn.get(k);
      if (!g) {
        g = {
          key: k, tracking: x.tracking_number, bc_order_id: x.bc_order_id,
          receiver_name: x.receiver_name, receiver_state: x.receiver_state,
          date: x.transaction_date, total: 0, lines: [],
        };
        byTn.set(k, g);
        groups.push(g);
      }
      g.total += Number(x.net_amount || 0);
      g.lines.push(x);
    }
    groups.forEach(g => {
      g.total = Math.round(g.total * 100) / 100;
      g.lines.sort((a, b) => Number(b.net_amount) - Number(a.net_amount));
    });
    groups.sort((a, b) => b.total - a.total);

    return (
      <div className="card">
        <div className="card-header">
          Adjustments &amp; Extras — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>
            charges UPS added after the fact (zone adjustments, address corrections, re-rates, audits)
            that the scale estimate couldn't have known. Net total{' '}
            <strong style={{ color: extraTotal > 0 ? 'var(--red)' : '#1a8a4a' }}>{fmtSigned(extraTotal)}</strong>
            {' '}across {groups.length} package{groups.length === 1 ? '' : 's'} ({extras.length} line{extras.length === 1 ? '' : 's'}).
          </span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {extrasLoading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
          ) : groups.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
              No adjustments or extra charges on this invoice — every charge is a standard rated charge.
            </div>
          ) : (
            <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th style={{ textAlign: 'left' }}>Tracking #</th>
                  <th style={{ textAlign: 'left' }}>Order</th>
                  <th style={{ textAlign: 'left' }}>Customer</th>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Charge</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const multi = g.lines.length > 1;
                  const isOpen = expandedExtra === g.key;
                  const top = g.lines[0];
                  const label = multi
                    ? `${top.charge_description} + ${g.lines.length - 1} more`
                    : top.charge_description;
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        style={{ cursor: multi ? 'pointer' : 'default' }}
                        onClick={() => multi && setExpandedExtra(isOpen ? null : g.key)}
                      >
                        <td style={{ textAlign: 'center' }}>
                          {multi && (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                        </td>
                        <td style={{ fontFamily: 'monospace' }}>{g.tracking || '—'}</td>
                        <td>{g.bc_order_id || <span style={{ color: 'var(--gray-400)' }}>—</span>}</td>
                        <td>{g.receiver_name || '—'}{g.receiver_state ? <span style={{ color: 'var(--gray-400)' }}> · {g.receiver_state}</span> : null}</td>
                        <td>{fmtDate(g.date)}</td>
                        <td>{label}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: g.total > 0 ? 'var(--red)' : g.total < 0 ? '#1a8a4a' : 'var(--gray-500)' }}>
                          {fmtSigned(g.total)}
                        </td>
                      </tr>
                      {multi && isOpen && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc', padding: 0 }}>
                            <table className="product-table" style={{ width: '100%', fontSize: 11, margin: 0 }}>
                              <tbody>
                                {g.lines.map((l) => {
                                  const amt = Number(l.net_amount || 0);
                                  return (
                                    <tr key={l.id}>
                                      <td style={{ width: 24 }}></td>
                                      <td>{fmtDate(l.transaction_date)}</td>
                                      <td>
                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: '#fff', background: CAT_COLOR[l.charge_category] || 'var(--gray-400)' }}>
                                          {l.charge_category}
                                        </span>
                                      </td>
                                      <td>{l.charge_description}</td>
                                      <td style={{ textAlign: 'right', fontWeight: 700, color: amt > 0 ? 'var(--red)' : amt < 0 ? '#1a8a4a' : 'var(--gray-500)' }}>
                                        {fmtSigned(amt)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {groups.length > 0 && (
          <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--gray-500)' }}>
            One row per package — expand a multi-line row to see each charge. Positive = extra charge to us · negative (green) = UPS credited us back.
          </div>
        )}
      </div>
    );
  };

  // ── Render: account-level charges tab ──────────────────────────────────────
  // The fees with no tracking number — billed to the account, not a package —
  // plus the footing that proves the whole bill reconciles to the penny.
  const renderAccountTab = () => {
    const a = accountCharges;
    const lines = (a && Array.isArray(a.lines)) ? a.lines : [];
    const invoiceAmount = a && a.invoice_amount != null ? Number(a.invoice_amount) : null;
    const packageTotal = a ? Number(a.package_total || 0) : 0;
    const accountTotal = a ? Number(a.account_total || 0) : 0;
    const accountAdj = a ? Number(a.account_adj_total || 0) : 0;
    const chargeTotal = a ? Number(a.charge_total || 0) : 0;
    const pkgCount = a ? Number(a.package_count || 0) : 0;
    const foots = invoiceAmount == null ? true : Math.abs(chargeTotal - invoiceAmount) < 0.01;
    const num = { fontVariantNumeric: 'tabular-nums' };

    return (
      <>
        {/* Footing — proves the bill reconciles to the penny */}
        <div className="card" style={{ marginBottom: 14, padding: '14px 18px' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--navy)', marginBottom: 2 }}>How this bill foots</div>
          <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 10 }}>
            Every dollar UPS billed, split into what's tied to a package vs what's billed straight to the account.
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>Package charges</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 8 }}>{pkgCount.toLocaleString()} packages · on the Reconciliation tab</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', ...num }}>{fmt(packageTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#6b3fa0' }}>Account-level fees</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 8 }}>{lines.length} fee{lines.length === 1 ? '' : 's'} not tied to a package · listed below</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#6b3fa0', ...num }}>{fmtSigned(accountTotal)}</span>
          </div>
          {Math.abs(accountAdj) > 0.005 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--orange)', cursor: 'pointer' }} onClick={() => setDetailTab('extras')}>Account-level adjustments</span>
                <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 8 }}>e.g. the monthly API/photo billing adjustment · shown in Adjustments &amp; Other</span>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--orange)', ...num }}>{fmtSigned(accountAdj)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderTop: '1px solid var(--gray-300)' }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--navy)' }}>Total of all charge lines</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', ...num }}>{fmt(chargeTotal)}</span>
          </div>
          {invoiceAmount != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0' }}>
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>UPS invoice total (header)</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: foots ? '#1a8a4a' : 'var(--red)', ...num }}>
                {fmt(invoiceAmount)}
                {foots
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> ties to the penny</span>
                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={14} /> off by {fmt(Math.abs(chargeTotal - invoiceAmount))}</span>}
              </span>
            </div>
          )}
        </div>

        {/* Account-level charge list */}
        <div className="card">
          <div className="card-header">
            Account-level charges — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>
              fees UPS bills to the account, not to any package — weekly pickup &amp; service fees,
              account-wide adjustments. This is where surprise fees hide. Net{' '}
              <strong style={{ color: accountTotal > 0 ? 'var(--red)' : '#1a8a4a' }}>{fmtSigned(accountTotal)}</strong>
              {' '}across {lines.length} item{lines.length === 1 ? '' : 's'}.
            </span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {accountLoading ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
            ) : lines.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
                No account-level charges on this invoice — every charge is tied to a package.
              </div>
            ) : (
              <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Charge</th>
                    <th style={{ textAlign: 'left' }}>Category</th>
                    <th style={{ textAlign: 'left' }}>Class</th>
                    <th style={{ textAlign: 'left' }}>Billing dates</th>
                    <th style={{ textAlign: 'right' }}>Times billed</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const amt = Number(l.total || 0);
                    const dates = l.first_date && l.last_date && l.first_date !== l.last_date
                      ? `${fmtDate(l.first_date)} – ${fmtDate(l.last_date)}`
                      : fmtDate(l.first_date);
                    const fee = accountFeeLabel(l.description, l.class_code);
                    return (
                      <tr key={`${l.description}-${l.class_code || ''}-${i}`}>
                        <td style={{ fontWeight: 600, color: 'var(--navy)' }}>
                          {fee ? (
                            <>
                              {fee.label}
                              <span style={{ display: 'block', fontWeight: 400, fontSize: 10.5, color: 'var(--gray-500)' }}>
                                {l.description} — {fee.note}
                              </span>
                            </>
                          ) : l.description}
                        </td>
                        <td>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: '#fff', background: CAT_COLOR[l.category] || '#6b3fa0' }}>
                            {l.category}
                          </span>
                        </td>
                        <td style={{ color: 'var(--gray-500)' }}>{l.class_code || '—'}</td>
                        <td style={{ color: 'var(--gray-500)' }}>{dates}</td>
                        <td style={{ textAlign: 'right' }}>{Number(l.line_count || 0).toLocaleString()}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: amt > 0 ? 'var(--red)' : amt < 0 ? '#1a8a4a' : 'var(--gray-500)' }}>
                          {fmtSigned(amt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--navy)' }}>Account-level total</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: accountTotal > 0 ? 'var(--red)' : '#1a8a4a' }}>{fmtSigned(accountTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          {lines.length > 0 && (
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--gray-500)' }}>
              Positive = charged to us · negative (green) = UPS credit. Spot a fee you didn't authorize?
              Dispute it in the UPS Billing Center → Dispute &amp; Refund History.
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Render: Service Refunds tab (the bill's "Service Refund" section) ──────
  // UPS GSR credits already applied on THIS bill for prior-week service
  // failures. (Forward-looking credits we're still chasing live in Late
  // Packages → status submitted/credited.)
  const renderServiceRefundsTab = () => {
    const total = serviceRefunds.reduce((s, l) => s + Number(l.net_amount || 0), 0);
    return (
      <>
      {creditMatches.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            Matched to refunds you filed — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>
              these GSR credits correspond to late packages we logged. Confirm the credit landed.
            </span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Tracking #</th>
                  <th style={{ textAlign: 'left' }}>Order</th>
                  <th style={{ textAlign: 'left' }}>Customer</th>
                  <th style={{ textAlign: 'left' }}>Kind</th>
                  <th style={{ textAlign: 'right' }}>Expected</th>
                  <th style={{ textAlign: 'right' }}>Credited</th>
                  <th style={{ textAlign: 'left' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {creditMatches.map((m) => (
                  <tr key={m.tracking_number} style={{ background: m.status === 'credited' ? '#f3faf6' : undefined }}>
                    <td style={{ fontFamily: 'monospace' }}>{m.tracking_number}</td>
                    <td>{m.bc_order_id || '—'}</td>
                    <td>{m.customer || '—'}</td>
                    <td>{m.late_kind === 'same_day' ? 'Same-day' : 'Whole-day'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(m.refund_amount)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a8a4a' }}>{fmtSigned(m.credit_amount)}</td>
                    <td style={{ color: m.status === 'credited' ? '#1a8a4a' : 'var(--orange)', fontWeight: 600 }}>{m.status}</td>
                    <td>
                      {m.status !== 'credited' && (
                        <button className="btn btn-outline" style={{ fontSize: 10, padding: '2px 8px' }}
                          title="Mark this filed refund as credited (it landed on this bill)"
                          onClick={() => updateRefundStatus(m.tracking_number, 'credited', `Credited ${fmt(m.credit_amount)} on invoice ${activeInvoice?.invoice_number}`)}>
                          <Check size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Mark credited
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--gray-500)' }}>
            The customer paid the shipping on the original bill; UPS applies the credit on a later one — this links the two so you can confirm what actually came back.
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header">
          Service Refunds — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>
            UPS GSR credits already applied on this invoice for prior-week late deliveries. Net{' '}
            <strong style={{ color: total < 0 ? '#1a8a4a' : 'var(--gray-500)' }}>{fmtSigned(total)}</strong>
            {' '}across {serviceRefunds.length} line{serviceRefunds.length === 1 ? '' : 's'}. Credits you're still
            waiting on (filed this week) are tracked in <strong>Late Packages</strong>.
          </span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {serviceRefundsLoading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
          ) : serviceRefunds.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
              No service-refund (GSR) credits on this invoice.
            </div>
          ) : (
            <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Tracking #</th>
                  <th style={{ textAlign: 'left' }}>Category</th>
                  <th style={{ textAlign: 'left' }}>Class</th>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th style={{ textAlign: 'right' }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {serviceRefunds.map((l) => {
                  const amt = Number(l.net_amount || 0);
                  return (
                    <tr key={l.id}>
                      <td>{fmtDate(l.transaction_date)}</td>
                      <td style={{ fontFamily: 'monospace' }}>{l.tracking_number || '—'}</td>
                      <td>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: '#fff', background: CAT_COLOR[l.charge_category] || 'var(--gray-400)' }}>
                          {l.charge_category}
                        </span>
                      </td>
                      <td>{l.charge_class_code}</td>
                      <td>{l.charge_description}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: amt < 0 ? '#1a8a4a' : 'var(--gray-500)' }}>{fmtSigned(amt)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--gray-200)', fontWeight: 700 }}>
                  <td colSpan={5} style={{ textAlign: 'right', color: 'var(--navy)' }}>Total credited back</td>
                  <td style={{ textAlign: 'right', color: '#1a8a4a' }}>{fmtSigned(total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      </>
    );
  };

  // ── Render: refunds & claims tab ───────────────────────────────────────────
  // Same-day lates (delivered after the committed time) → billing-credit REFUND;
  // whole-day lates → CLAIM. UPS has no refund API, so this detects + preps the
  // dispute and tracks status; submission is manual in the Billing Center.
  const renderRefundsTab = () => {
    const sameDay = refunds.filter(r => r.late_kind === 'same_day');
    const wholeDay = refunds.filter(r => r.late_kind === 'whole_day');
    const sum = (arr) => arr.reduce((s, r) => s + Number(r.refund_amount || 0), 0);
    // "Handled" = a claim's been filed (whole-day) or the refund's been
    // credited/denied/submitted. Push those to the BOTTOM so the rows still
    // needing action stay up top and you can run straight down the list.
    // (Array.sort is stable, so within each group the existing order is kept.)
    const isHandled = (r) => {
      if (r.claim_number) return true;
      const s = String(r.status || '');
      return s === 'credited' || s === 'denied' || s === 'submitted';
    };
    const orderedRefunds = [...refunds].sort((a, b) => (isHandled(a) ? 1 : 0) - (isHandled(b) ? 1 : 0));
    const REF_STATUS = {
      flagged: { label: 'Flagged', color: 'var(--orange)' },
      submitted: { label: 'Submitted', color: 'var(--navy)' },
      credited: { label: 'Credited', color: '#1a8a4a' },
      denied: { label: 'Denied', color: 'var(--red)' },
    };
    // Copy just the tracking number (that's all the Billing Center dispute
    // form needs to look the shipment up).
    const copyPacket = (r) => {
      const text = String(r.tracking_number || '');
      const flash = (ok) => {
        if (ok) { setCopiedTracking(r.tracking_number); setTimeout(() => setCopiedTracking(null), 1600); }
        else { window.prompt('Tracking number:', text); }
      };
      // Try the async Clipboard API; fall back to execCommand (older/non-secure
      // contexts), and finally a prompt so the text is never just lost.
      const tryExec = () => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text; ta.style.position = 'fixed'; ta.style.top = '-1000px'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.focus(); ta.select();
          const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
        } catch (_) { return false; }
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => flash(true)).catch(() => flash(tryExec()));
      } else {
        flash(tryExec());
      }
    };

    return (
      <>
        <div className="card" style={{ marginBottom: 14, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--navy)', maxWidth: 720 }}>
            <Info size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            <strong>Same-day lates</strong> (delivered after the committed time for time-definite services) get a UPS
            billing-credit <strong>refund</strong>; <strong>whole-day lates</strong> get a <strong>claim</strong>. UPS has no
            refund API — this detects them and preps the dispute; submit in the UPS Billing Center within <strong>15 days</strong>.
          </div>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
            disabled={refundScan?.status === 'running'} onClick={runRefundScan}>
            <RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {refundScan?.status === 'running' ? 'Scanning…' : 'Scan for refunds'}
          </button>
        </div>

        {refundScan && (
          <div style={{ marginBottom: 12, fontSize: 12, color: refundScan.status === 'error' ? 'var(--red)' : refundScan.status === 'done' ? '#1a8a4a' : 'var(--navy)' }}>
            {refundScan.status === 'error'
              ? <><AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> {refundScan.message || 'Scan failed'}</>
              : refundScan.status === 'done'
                ? <><CheckCircle size={13} style={{ verticalAlign: 'middle' }} /> Scan complete — checked {refundScan.checked || 0}, found {refundScan.same_day || 0} same-day + {refundScan.whole_day || 0} whole-day.</>
                : <><RefreshCw size={13} style={{ verticalAlign: 'middle' }} /> Checking packages… {refundScan.checked || 0} checked, {refundScan.same_day || 0} same-day + {refundScan.whole_day || 0} whole-day so far (pulls UPS delivery times + commit times — give it a minute).</>}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 14 }}>
          <div className="card" style={{ padding: '10px 14px', background: '#fff8e1' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Same-day late → Refund</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orange)' }}>{sameDay.length}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{fmt(sum(sameDay))} recoverable</div>
          </div>
          <div className="card" style={{ padding: '10px 14px', background: '#fde8e8' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#c62828', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Whole-day late → Claim</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#c62828' }}>{wholeDay.length}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{fmt(sum(wholeDay))} recoverable</div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Detected late deliveries — <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>same-day lates → <strong>Copy tracking</strong> + <strong>Request Credit</strong> (Billing Center); whole-day lates → <strong>File Claim</strong> (shipping comes back with the claim). Then mark the status.</span></div>
          <div className="card-body" style={{ padding: 0 }}>
            {refundLoading ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
            ) : refunds.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
                No late deliveries detected yet — click <strong>Scan for refunds</strong> to check this invoice's packages against committed vs delivered times.
              </div>
            ) : (
              <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 24 }}></th>
                    <th style={{ textAlign: 'left' }}>Kind</th>
                    <th style={{ textAlign: 'left' }}>Tracking #</th>
                    <th style={{ textAlign: 'left' }}>Order</th>
                    <th style={{ textAlign: 'left' }}>Customer</th>
                    <th style={{ textAlign: 'left' }}>Service</th>
                    <th style={{ textAlign: 'left' }}>Committed</th>
                    <th style={{ textAlign: 'left' }}>Delivered</th>
                    <th style={{ textAlign: 'right' }}>Late</th>
                    <th style={{ textAlign: 'right' }}>Refund</th>
                    <th style={{ textAlign: 'left' }}>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orderedRefunds.map((r) => {
                    const meta = REF_STATUS[r.status] || REF_STATUS.flagged;
                    const isSame = r.late_kind === 'same_day';
                    const isWhole = r.late_kind === 'whole_day';
                    // Whole-day resolution mode. Honor an explicit choice; with no
                    // choice yet, default to "claim" (ready to file) when a reship
                    // already exists — those are ready to go — otherwise "waiting"
                    // (still need a reship or refund before we can file). Refunds
                    // aren't detectable here (they live only in the live Auth.net
                    // feed), so a refunded-but-not-reshipped row starts as Waiting
                    // until you flip the dropdown to File Claim.
                    const explicitMode = (r.resolution_mode === 'credit' || r.resolution_mode === 'claim' || r.resolution_mode === 'waiting') ? r.resolution_mode : null;
                    const hasRefund = refundOrderIds.has(String(r.order_id));
                    const wholeMode = !isWhole ? null : (explicitMode || ((r.has_reship || hasRefund) ? 'claim' : 'waiting'));
                    const creditMode = isSame || (isWhole && wholeMode === 'credit');
                    // Choose One / Credited / Denied — refund status tracking.
                    const refundStatusSelect = (
                      <select value={r.status === 'flagged' ? '' : r.status} onChange={(e) => updateRefundStatus(r.tracking_number, e.target.value || 'flagged')}
                        className="form-select" style={{ fontSize: 11, padding: '2px 4px', color: meta.color, fontWeight: 600 }}>
                        <option value="">Choose One</option>
                        <option value="credited">Credited</option>
                        <option value="denied">Denied</option>
                      </select>
                    );
                    // Copy tracking + Request Credit (the UPS Billing Center GSR dispute).
                    const refundActions = (
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <button
                          className={copiedTracking === r.tracking_number ? 'btn btn-primary' : 'btn btn-outline'}
                          style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}
                          title="Copy the tracking number to paste into the UPS Billing Center dispute form"
                          onClick={() => copyPacket(r)}>
                          {copiedTracking === r.tracking_number
                            ? <><Check size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />Copied!</>
                            : 'Copy tracking'}
                        </button>
                        <a href="https://billing.ups.com/ups/billing/dispute" target="_blank" rel="noopener noreferrer"
                          title="Copies the tracking # and opens the UPS Billing Center — click 'Need to Request a Service Refund?' there and paste"
                          onClick={() => copyPacket(r)}
                          style={{ fontSize: 10, color: copiedTracking === r.tracking_number ? '#1a8a4a' : 'var(--orange)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {copiedTracking === r.tracking_number
                            ? <><Check size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />Copied — opening…</>
                            : 'Request Credit'}
                        </a>
                      </div>
                    );
                    const isOpen = expandedRefund === r.tracking_number;
                    const detail = refundDetail[r.tracking_number];
                    return (
                      <React.Fragment key={r.tracking_number}>
                      <tr>
                        <td style={{ textAlign: 'center', cursor: 'pointer', color: 'var(--gray-500)' }}
                          title="Order number, split/reship, and Problems notes"
                          onClick={() => toggleRefundDetail(r)}>
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, color: '#fff', background: isSame ? 'var(--orange)' : '#c62828' }}>
                            {isSame ? 'SAME-DAY' : 'WHOLE-DAY'}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'monospace' }}>{r.tracking_number}</td>
                        <td>
                          {(r.bc_order_id && r.bc_order_id !== '0') ? r.bc_order_id
                            : r.orig_bc_order_id ? (
                              <span title={`Reship — original order #${r.orig_bc_order_id} (no BigCommerce number of its own)`}>
                                {r.orig_bc_order_id}<span style={{ fontSize: 9, color: 'var(--gray-400)', marginLeft: 3 }}>(reship)</span>
                              </span>
                            ) : '—'}
                        </td>
                        <td>{r.customer || '—'}{r.state ? <span style={{ color: 'var(--gray-400)' }}> · {r.state}</span> : null}</td>
                        <td>{r.service || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.committed_time || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.delivered_at)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--red)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {isSame ? `${r.minutes_late}m` : `${r.days_late}d`}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.refund_amount)}</td>
                        <td>
                          {creditMode ? refundStatusSelect : (
                            // Whole-day claim mode: the claim record tells us if it's
                            // filed; approvals/denials live in Claims.
                            r.claim_number ? (
                              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy)' }} title={`Claim ${r.claim_number}`}>
                                {r.claim_upsc || r.claim_status || 'Submitted'}
                              </span>
                            ) : wholeMode === 'waiting' ? (
                              // No reship/refund yet — waiting on the customer.
                              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange)' }} title="Waiting on the customer — no reship or refund yet">Waiting</span>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Not filed</span>
                            )
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {isSame ? refundActions : (
                            // Whole-day resolution (wholeMode computed above): "waiting"
                            // is NON-actionable (no reship/refund yet); "claim" shows
                            // File Claim; "credit" shows the shipping-credit actions.
                            <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                              <select value={wholeMode}
                                onChange={(e) => setResolutionMode(r.tracking_number, e.target.value)}
                                className="form-select" style={{ fontSize: 10, padding: '2px 4px', fontWeight: 600 }}>
                                <option value="waiting">Waiting</option>
                                <option value="claim">File Claim</option>
                                <option value="credit">Shipping Credit Only</option>
                              </select>
                              {wholeMode === 'credit' ? refundActions : r.claim_number ? (
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#1a8a4a', whiteSpace: 'nowrap' }} title={`Claim ${r.claim_number}`}>
                                  <Check size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />Submitted
                                </span>
                              ) : wholeMode === 'waiting' ? (
                                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', whiteSpace: 'nowrap', padding: '2px 8px', border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f8fafc' }}
                                  title="Waiting on the customer. Once you know it's a reship/refund pick File Claim; if they were fine pick Shipping Credit Only.">
                                  Waiting
                                </span>
                              ) : (
                                <button className="btn btn-outline" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}
                                  title="File a UPS claim — the shipping comes back with the claim"
                                  disabled={!r.order_id} onClick={() => fileClaimFromRefund(r)}>
                                  File Claim
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={12} style={{ background: '#f8fafc', padding: '10px 16px' }}>
                            {(() => {
                              const sp = detail && detail.split;
                              const splitLabel = sp && sp.splitOriginal
                                ? <>Split order</>
                                : sp && sp.reshipOriginal
                                ? <>Reship{sp.bcOriginal ? <> — original <strong>#{sp.bcOriginal}</strong></> : null}</>
                                : <span style={{ color: 'var(--gray-400)' }}>not a split/reship</span>;
                              return (
                                <div style={{ fontSize: 12, color: 'var(--navy)', display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 3 }}>Order</div>
                                    <strong>#{r.bc_order_id || '—'}</strong>
                                    <div style={{ marginTop: 3 }}>{detail && detail.loading ? '…' : splitLabel}</div>
                                  </div>
                                  <div style={{ flex: 1, minWidth: 320 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 3 }}>Problems</div>
                                    {detail && detail.loading ? '…'
                                      : (detail && detail.problems && detail.problems.length) ? (
                                        detail.problems.map((p, i) => (
                                          <div key={i} style={{ marginBottom: 5, paddingBottom: 5, borderBottom: i < detail.problems.length - 1 ? '1px solid var(--gray-200)' : 'none' }}>
                                            <span style={{ fontWeight: 700 }}>{p.type || 'Problem'}</span>
                                            <span style={{ color: 'var(--gray-500)' }}> · {p.date}{p.employee ? ` · ${p.employee}` : ''}</span>
                                            <div>{p.description || '—'}</div>
                                            {p.response ? <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>↳ {p.response}</div> : null}
                                          </div>
                                        ))
                                      ) : <span style={{ color: 'var(--gray-400)' }}>No problems recorded.</span>}
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {refunds.length > 0 && (
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--gray-500)' }}>
              "Copy tracking" copies the tracking number; "Request Credit" opens the UPS Billing Center dispute form (paste the tracking, submit). Then set the status so nothing slips past the 15-day window.
            </div>
          )}
        </div>
      </>
    );
  };

  // ── Render: detail (header + tabs) ─────────────────────────────────────────
  const renderDetail = () => {
    const inv = activeInvoice;
    const total = reconSummary.total;
    return (
      <>
        <button
          onClick={() => { try { sessionStorage.removeItem('upsBillActiveInvoice'); } catch (_) {} stopPoll(); setBackfilling(false); setBackfillResult(null); setView('list'); setActiveInvoice(null); }}
          className="btn btn-ghost"
          style={{ fontSize: 12, padding: '4px 8px', marginBottom: 12 }}
        >
          <ChevronLeft size={14} style={{ verticalAlign: 'middle' }} /> Back to invoices
        </button>

        {inv && (
          <>
            {/* Header */}
            <div className="card" style={{ marginBottom: 14, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>
                    Reconcile invoice {inv.invoice_number}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                    {inv.carrier} · Account {inv.account_number} · Invoiced {fmtDate(inv.invoice_date)} · Due {fmtDate(inv.due_date)}
                  </div>
                </div>
                {/* Summary of Charges — the UPS paper bill's four buckets, which
                    foot to the invoice total. WorldShip is the residual, so these
                    ALWAYS sum to the total (matches the bill line-for-line). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
                  {summaryOfCharges && (() => {
                    const s = summaryOfCharges;
                    const rows = [
                      { label: 'Package cost', value: fmt(s.world_ship), sub: total ? `vs scale ${fmt(total.scale_total)}` : null, color: 'var(--navy)' },
                      { label: 'Adjustments & Other', value: fmtSigned(s.adjustments_other), color: Math.abs(Number(s.adjustments_other)) > 0.005 ? 'var(--orange)' : 'var(--navy)', onClick: () => setDetailTab('extras') },
                      { label: 'Service Refunds', value: fmtSigned(s.service_refund), color: '#1a8a4a', onClick: () => setDetailTab('service_refunds') },
                      { label: 'Service Charges', value: fmtSigned(s.service_charges), color: '#6b3fa0', onClick: () => setDetailTab('account') },
                    ];
                    return (
                      <div style={{ fontSize: 13, minWidth: 290 }}>
                        {rows.map((r, i) => (
                          <div key={i} onClick={r.onClick}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 18, padding: '3px 0', cursor: r.onClick ? 'pointer' : 'default' }}>
                            <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{r.label}
                              {r.sub && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--gray-500)', marginLeft: 6 }}>{r.sub}</span>}
                            </span>
                            <strong style={{ fontWeight: 800, color: r.color, fontVariantNumeric: 'tabular-nums' }}>{r.value}</strong>
                          </div>
                        ))}
                        {explained && Number(explained.sim_count) > 0 && (
                          <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4, fontStyle: 'italic' }}>
                            Package cost already nets {explained.sim_count} special-incentive credits ({fmtSigned(explained.sim_total)})
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', whiteSpace: 'nowrap' }}>{fmt(inv.charge_total)}</div>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                <button
                  onClick={() => setDetailTab('reconcile')}
                  className={detailTab === 'reconcile' ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '5px 14px' }}
                >Reconciliation</button>
                <button
                  onClick={() => setDetailTab('extras')}
                  className={detailTab === 'extras' ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '5px 14px' }}
                >
                  <FileWarning size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                  Adjustments &amp; Other{total && total.extra_pkgs ? ` (${total.extra_pkgs})` : ''}
                </button>
                <button
                  onClick={() => setDetailTab('service_refunds')}
                  className={detailTab === 'service_refunds' ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '5px 14px' }}
                >
                  <DownloadCloud size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                  Service Refunds{serviceRefunds.length ? ` (${serviceRefunds.length})` : ''}
                </button>
                <button
                  onClick={() => setDetailTab('account')}
                  className={detailTab === 'account' ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '5px 14px' }}
                >
                  <Receipt size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                  Service Charges{accountCharges && accountCharges.account_line_count ? ` (${accountCharges.account_line_count})` : ''}
                </button>
                <button
                  onClick={() => setDetailTab('refunds')}
                  className={detailTab === 'refunds' ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ fontSize: 12, padding: '5px 14px' }}
                >
                  <Clock size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                  Late Packages{refunds.length ? ` (${refunds.length})` : ''}
                </button>
              </div>
            </div>

            {detailTab === 'reconcile' ? renderReconcileTab()
              : detailTab === 'extras' ? renderExtrasTab()
              : detailTab === 'service_refunds' ? renderServiceRefundsTab()
              : detailTab === 'account' ? renderAccountTab()
              : renderRefundsTab()}
          </>
        )}
        {!inv && (
          <div className="card" style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading invoice…</div>
        )}
      </>
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>UPS Bills</h1>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          Import a UPS bill, then reconcile it: the core charges vs the scale cost per package —
          with late deliveries and after-the-fact adjustments broken out separately.
        </div>
      </div>
      {view === 'list' ? renderList() : renderDetail()}
      {claimModal && (
        <NewClaimModal
          prefillOrder={claimModal}
          onClose={() => setClaimModal(null)}
          onSubmitted={() => {
            // The row we just filed lives in the Late Packages (refunds) list —
            // reload THAT so the claim_number lands and its File Claim button
            // flips to "✓ Submitted". Also refresh the recon grid.
            if (activeInvoice) loadRefunds(activeInvoice.invoice_number);
            loadRecon();
          }}
        />
      )}
    </div>
  );
}
